import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';

const sections = [
  {
    num: '1',
    title: 'Information we may Collect',
    paragraphs: [
      'When you submit an enquiry through our website, contact us through WhatsApp, call us, email us, or communicate with us through another channel, we may collect:',
      {
        type: 'bullets',
        items: [
          'Your name',
          'Your phone number',
          'Whether you are a tenant or owner',
          'The property location or preferred area',
          'Your rental requirements',
          'Information included in your message',
          'Records of communications relating to your enquiry',
        ],
      },
      'As the rental process progresses, we may also receive additional information that you choose to provide, including:',
      {
        type: 'bullets',
        items: [
          'Property information',
          'Identification or address documents',
          'Ownership-related documents',
          'Information required for rental agreements',
          'Information required for registration or biometric appointments',
          'Information required for police verification or society procedures',
          'Payment or transaction records, where applicable',
        ],
      },
      'Depending on whether you are a tenant or owner, this may include documents such as Aadhaar Card, PAN Card, Work ID, Index II, electricity bills, possession or parking letters, and other documents reasonably required for verification, registration, police verification, society procedures, or completion of the rental process.',
      'Please provide only information that is accurate and necessary for the service you require.',
    ],
  },
  {
    num: '2',
    title: 'How we use your Information',
    paragraphs: [
      'We may use your information to:',
      {
        type: 'bullets',
        items: [
          'Respond to your enquiry',
          'Understand your rental or property requirements',
          'Contact you by phone, WhatsApp, or email',
          'Suggest relevant rental properties or prospective tenants',
          'Coordinate property visits',
          'Coordinate communication between tenants and owners',
          'Assist with rental agreements and registration',
          'Coordinate biometric appointments',
          'Assist with police verification and society procedures',
          'Arrange related services such as basic cleaning or pest control',
          'Maintain enquiry and service records',
          'Follow up regarding your existing enquiry',
          'Complete an ongoing rental transaction',
          'Respond to questions, concerns, or complaints',
          'Meet applicable legal or regulatory requirements',
        ],
      },
      'We do not use enquiry details to send unsolicited promotional WhatsApp messages or marketing emails.',
      'You may contact SettleSpace whenever you require rental assistance or wish to rent out a property.',
    ],
  },
  {
    num: '3',
    title: 'How we store Information',
    paragraphs: [
      'Information relating to an enquiry or service may be stored through tools used for communication and business record-keeping, including:',
      {
        type: 'bullets',
        items: [
          'WhatsApp',
          'Phone contacts',
          'Google Sheets',
          'Email',
          'Website-form or hosting infrastructure',
          'Other necessary business records',
        ],
      },
      'These third-party platforms may process information according to their own privacy policies and terms.',
    ],
  },
  {
    num: '4',
    title: 'When Information may be Shared',
    paragraphs: [
      'Where necessary to assist with a rental enquiry or transaction, relevant information may be shared with:',
      {
        type: 'bullets',
        items: [
          'A prospective tenant',
          'An owner',
          'Agreement-drafting or registration service providers',
          'Biometric service providers',
          'Police-verification contacts',
          'Society representatives or coordination contacts',
          'Cleaning or pest-control providers',
          'Other professionals involved in completing the requested service',
          'Government, regulatory, or legal authorities where disclosure is required by law',
        ],
      },
      'Before sharing your phone number or other contact details with another party for the rental process, we will inform you that the information is being shared.',
      'Only information reasonably required for the relevant purpose will be shared.',
      'SettleSpace does not sell or rent your personal information. We do not share it with third parties for unrelated advertising or marketing.',
    ],
  },
  {
    num: '5',
    title: 'Cookies and Website Analytics',
    paragraphs: [
      'SettleSpace does not knowingly use Google Analytics, Meta Pixel, advertising trackers, or similar behavioural advertising technologies on this website.',
      'The website, hosting provider, or embedded services may still use essential technical technologies needed for:',
      {
        type: 'bullets',
        items: [
          'Loading and displaying the website',
          'Website security and fraud prevention',
          'Maintaining website performance',
          'Processing contact-form submissions',
          'Remembering essential technical preferences',
        ],
      },
      'SettleSpace does not intend to use these technologies to create advertising profiles about website visitors.',
      'If analytics, advertising tools, or other non-essential tracking technologies are introduced in the future, this Privacy Policy will be updated. Any consent mechanism required by applicable law will also be introduced.',
    ],
  },
  {
    num: '6',
    title: 'How long we retain Information',
    paragraphs: [
      'We retain personal information only for as long as reasonably necessary to:',
      {
        type: 'bullets',
        items: [
          'Respond to an enquiry',
          'Provide the requested service',
          'Complete pending work',
          'Maintain relevant transaction records',
          'Resolve disputes or complaints',
          'Meet legal, accounting, or regulatory requirements',
        ],
      },
      'The retention period may vary depending on the nature and status of the enquiry or transaction.',
      'When information is no longer reasonably required, we may delete it, anonymise it, or securely remove it from active records.',
    ],
  },
  {
    num: '7',
    title: 'Your Choices and Requests',
    paragraphs: [
      'You may contact SettleSpace to request:',
      {
        type: 'bullets',
        items: [
          'Access to information held about you',
          'Correction of inaccurate information',
          'Updating of outdated information',
          'Deletion of your personal information',
          'Clarification about how your information has been used',
          'Clarification about whether your information has been shared',
          'Withdrawal of consent where our use of information depends on your consent',
        ],
      },
      'Where your enquiry or transaction is complete and there is no legal or operational reason to retain the information, we will take reasonable steps to remove it from our active records.',
      'We may continue retaining information where:',
      {
        type: 'bullets',
        items: [
          'Work requested by you is still pending',
          'The information is needed to complete an ongoing transaction',
          'The information is required to resolve a dispute',
          'It forms part of necessary transaction or financial records',
          'Retention is required by applicable law',
        ],
      },
      'To process your request safely, we may need to verify that the request is being made by the person concerned.',
    ],
  },
  {
    num: '8',
    title: 'Information Security',
    paragraphs: [
      'SettleSpace takes reasonable organisational and practical measures to protect personal information against:',
      {
        type: 'bullets',
        items: [
          'Unauthorised access',
          'Misuse',
          'Accidental loss',
          'Alteration',
          'Unauthorised disclosure',
        ],
      },
      'Where sensitive documents are required for a rental or verification process, SettleSpace will take reasonable steps to limit access to those documents to people who need them for the relevant purpose.',
      'However, no internet transmission, messaging platform, or electronic storage system can be guaranteed to be completely secure.',
      'Please avoid sending sensitive documents that are not necessary for the requested service. Inform us promptly if you believe information shared with SettleSpace has been compromised.',
    ],
  },
  {
    num: '9',
    title: 'Third-party Links and Platforms',
    paragraphs: [
      'The website may contain links to external services such as:',
      {
        type: 'bullets',
        items: [
          'WhatsApp',
          'Social-media platforms',
          'Map services',
          'Other third-party websites or services',
        ],
      },
      'SettleSpace does not control the privacy practices, security, or content of these third parties.',
      'Your use of an external platform is governed by that platform\'s own privacy policy and terms.',
    ],
  },
  {
    num: '10',
    title: 'Information about other people',
    paragraphs: [
      'Please do not provide another person\'s personal information unless:',
      {
        type: 'bullets',
        items: [
          'It is necessary for the relevant rental process',
          'You are authorised to provide it',
          'You have informed the person appropriately',
        ],
      },
      'For example, a tenant or owner should not share another person\'s identification documents or contact details without an appropriate reason and permission.',
    ],
  },
  {
    num: '11',
    title: 'Children\'s Privacy',
    paragraphs: [
      'SettleSpace\'s website and services are not intended to collect personal information directly from children.',
      'Where information relating to a minor is necessary for a legitimate rental or documentation process, it should be provided only by or with the involvement of the minor\'s parent or lawful guardian.',
    ],
  },
  {
    num: '12',
    title: 'Changes to this Privacy Policy',
    paragraphs: [
      'We may update this Privacy Policy when our services, technology, data practices, or legal obligations change.',
      'The latest version will be published on this page with an updated effective date.',
      'Where appropriate, important changes may also be communicated through an additional notice.',
    ],
  },
  {
    num: '13',
    title: 'Contact and Privacy Requests',
    paragraphs: [
      'For questions, corrections, deletion requests, or concerns regarding this Privacy Policy or your personal information, contact:',
      'SettleSpace',
      'Email: hitesh@settlespace.in',
      'Website: https://settlespace.in',
      'Service area: Navi Mumbai, Maharashtra, India',
      'Please provide enough information for us to identify the relevant enquiry and understand your request.',
    ],
  },
];

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://settlespace.in';

  const goBack = () => {
    navigate('/');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById('site-footer')?.scrollIntoView({ behavior: 'auto' });
      });
    });
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy',
    url: `${siteUrl}/privacy-policy`,
    description:
      'How we collect, use, store, and protect personal information for rental services in Navi Mumbai.',
    inLanguage: 'en',
    isPartOf: {
      '@type': 'WebSite',
      name: 'SettleSpace',
      url: siteUrl,
    },
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <JsonLd data={jsonLd} />
      <div className="w-full px-6 md:px-12 lg:px-16 py-28 md:py-32">
        <div className="max-w-3xl mx-auto">
          <div className="bg-background-100 rounded-[48px] px-8 md:px-14 py-10 md:py-14">
          {/* Back to Home link */}
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-950 transition-colors mb-10 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            <span>Back to Home</span>
          </button>

          {/* Main Heading */}
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4 tracking-tight">
            Privacy Policy
          </h1>

          {/* Effective Date */}
          <p className="text-sm text-foreground-500 mb-12 md:mb-16">
            Effective date: 5 August 2026
          </p>

          {/* Intro */}
          <p className="text-sm md:text-base text-foreground-800 leading-relaxed mb-5">
            SettleSpace respects your privacy and is committed to handling your personal information responsibly and transparently.
          </p>
          <p className="text-sm md:text-base text-foreground-800 leading-relaxed mb-14">
            This Privacy Policy explains what information we may collect, how we use it, when it may be shared, how long it may be retained, and the choices available to you when you use our website or communicate with us.
          </p>

          {/* Sections */}
          <div className="flex flex-col gap-10 md:gap-12">
            {sections.map((section) => (
              <section key={section.num}>
                <h2 className="font-googleSans font-bold text-lg md:text-xl text-foreground-950 mb-4">
                  {section.num}. {section.title}
                </h2>
                <div className="flex flex-col gap-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  {section.paragraphs.map((para, idx) => {
                    if (typeof para === 'string') {
                      return <p key={idx}>{para}</p>;
                    }
                    if (para.type === 'bullets') {
                      return (
                        <ul key={idx} className="flex flex-col gap-2 pl-5">
                          {para.items.map((item, i) => (
                            <li key={i} className="relative pl-4 before:content-['•'] before:absolute before:left-0 before:text-foreground-400">
                              {item}
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    return null;
                  })}
                </div>
              </section>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-14 md:mt-16 pt-10 border-t border-background-200/60">
            <button
              type="button"
              onClick={goBack}
              className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 rounded-md text-sm font-medium hover:scale-105 hover:bg-foreground-800 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Back to SettleSpace
            </button>
          </div>
          </div>
        </div>
      </div>
    </>
  );
}